import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useT } from '../i18n';
import './BackBar.css';

// ── BACK BAR ─────────────────────────────────────────────────────────────────
// A thin strip under the main header carrying one Back control, on every page
// that isn't the dashboard.
//
// A BUTTON, not a link, and it prefers history over a fixed destination: a
// visitor who reached a designer profile from search expects Back to return to
// their results — with their query and filters intact, which a hard link to
// /dashboard would throw away.
//
// The fallback matters though. Arriving directly on a profile (a shared link, a
// new tab) leaves no history to go back to, and `navigate(-1)` would either do
// nothing or bounce out of the app entirely. `fallback` is used in that case.
// `width` must match the <main> it sits above. Every page centres its content
// at a different max-width (560 to 1360), so a single fixed gutter would leave
// the button floating away from the column it belongs to on most of them.
//
// `wide` is for the two pages built on `.dash-main--wide` (the dashboard and
// the profile). They take their geometry from the SAME custom properties that
// column does rather than repeating the number here — which is what went
// wrong: the column grew to 2000px, this stayed at 1240, and the arrow ended
// up hundreds of pixels inside the content it points away from.
// `hideOnMobile` is for pages that are a TAB on phones (see MobileTabBar).
// Back means nothing on a top-level destination — you did not come from
// anywhere, you tapped it — and the strip only costs a row of screen. Those
// same pages are still reached by navigation on desktop, where there is no tab
// bar, so the control stays there.
// `onBack` replaces the whole behaviour, for a page where leaving means more
// than moving in history — the product page reopens the conversation a share
// card was tapped in, which going back alone would leave closed. Optional, so
// every other caller keeps the history-then-fallback rule below.
export default function BackBar({
  fallback = '/dashboard', label, width = 1240, hideOnMobile = false, wide = false,
  onBack,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();

  function goBack() {
    if (onBack) { onBack(); return; }
    // `idx` is React Router's position in its own history stack. 0 means this
    // entry is the first one — there is nothing of ours behind it.
    const canGoBack = location.key !== 'default' && (window.history.state?.idx ?? 0) > 0;
    if (canGoBack) navigate(-1);
    else navigate(fallback, { replace: true });
  }

  // TWO elements, not one. The outer bar is what sticks, so its background has
  // to span the full viewport — page content scrolling under a background that
  // stopped at `width` would show through on both sides. The inner row carries
  // the max-width that keeps the button aligned with the content column.
  return (
    <div className={`bkb-bar${hideOnMobile ? ' bkb-bar--desktop' : ''}`}>
      {/* No inline width in the wide variant — an inline style would beat the
          custom properties the class reads the column's geometry from. */}
      <div
        className={`bkb${wide ? ' bkb--wide' : ''}`}
        style={wide ? undefined : { maxWidth: width }}
      >
        <button type="button" className="bkb-btn" onClick={goBack}>
          <svg viewBox="0 0 24 24" fill="none" width="17" height="17"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {label || t('back')}
        </button>
      </div>
    </div>
  );
}
